// src/components/page/memberPage/MemberPage.tsx
import React, {useEffect, useMemo} from 'react';
import {useParams, Link, useNavigate, useOutletContext} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {
    fetchMembers,
    selectMembersStatus,
    selectMembersError,
    selectMemberById,
    selectMemberByPathname,
} from '@/features/members/membersSlice';
import type {RootState} from '@/store';
import type {Member} from '@/types/member';
import {Box, Button, Card, Container, Grid, Group, Image, Loader, Stack, Text, Title} from '@mantine/core';
import {useBodyClass} from '@/hooks';
import classes from './MemberPage.module.scss';
import type {LayoutContext} from '@/layout/Layout';
import {TitleDecor} from "@/components/TitleDecor/TitleDecor";

// @ts-ignore
import LineSvg from '@/assets/images/line-1.svg?react'
// @ts-ignore
import SquareSvg from '@/assets/images/square.svg?react'
// @ts-ignore
import TriangleSvg from '@/assets/images/tringle-about-top.svg?react'

type RouteParams = { id?: string; pathname?: string; slug?: string };

const fullName = (m: Member) => {
    const parts = [m.first_name, m.last_name].filter(Boolean) as string[];
    return (parts.length ? parts.join(' ') : m.name) ?? 'Member';
};

export const MemberPage: React.FC = () => {
    useBodyClass(classes.meetTheProPage);
    const {headerH} = useOutletContext<LayoutContext>();

    const {id, pathname, slug} = useParams<RouteParams>();
    const key = pathname ?? slug ?? id ?? '';
    const looksLikeId = /^\d+$/.test(key);

    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const status = useAppSelector(selectMembersStatus);
    const error = useAppSelector(selectMembersError);

    // Параметризованный селектор без any
    const memberSelector = useMemo(() => {
        if (!key) return (_: RootState) => undefined as Member | undefined;
        return (s: RootState) =>
            looksLikeId ? selectMemberById(s, key) : selectMemberByPathname(s, key);
    }, [key, looksLikeId]);

    const member = useAppSelector(memberSelector);

    useEffect(() => {
        if (status === 'idle') dispatch(fetchMembers());
    }, [status, dispatch]);

    // const sectionStyle = {paddingTop: `calc(20px + ${headerH}px)`};

    let content: React.ReactNode;

    if (status === 'loading' && !member) {
        content = <Loader/>
    } else if (status === 'failed') {
        content = (
            <>
                <Stack gap="md">
                    <Text c="red">Error: {String(error ?? 'Load failed')}</Text>
                </Stack>
            </>
        );
    } else if (!member) {
        content = (
            <>
                <Stack gap="md">
                    <Title order={3} c="red">
                        Member not found
                    </Title>
                </Stack>
            </>
        );
    } else {
        const title = fullName(member);
        const photo = member.photo || member.avatar || member.thumb || undefined;

        const socials: Array<{ label: string; href: string }> = [
            ...(member.instagram ? [{label: 'Instagram', href: member.instagram}] : []),
            ...(member.youtube ? [{label: 'YouTube', href: member.youtube}] : []),
            ...(member.website ? [{label: 'Website', href: member.website}] : []),
        ];

        content = (
            <>

                <TriangleSvg className={classes.effectTriangle}/>
                <Grid className={classes.ourPro} gutter="lg">
                    <Grid.Col className={classes.ourProRow} span={{base: 12, md: 4}}>
                        <Card withBorder={false} bg={'transparent'} radius={0} styles={{root: {overflow: 'visible'}}}>
                            <Stack align="center" gap="sm">
                                {photo && (
                                    <Box className={classes.photo__cover}>
                                        <LineSvg className={classes.effect1}/>
                                        <SquareSvg className={classes.effect3}/>
                                        <Image className={classes.photo} src={photo} alt={title} w="100%" maw={450}/>
                                    </Box>
                                )}
                                {/*<Title order={3} ta="center">*/}
                                {/*    {title}*/}
                                {/*</Title>*/}
                                {/*{member.title && (*/}
                                {/*    <Text c="dimmed" ta="center">*/}
                                {/*        {member.title}*/}
                                {/*    </Text>*/}
                                {/*)}*/}


                            </Stack>
                        </Card>
                    </Grid.Col>

                    <Grid.Col span={{base: 12, md: 8}}>
                        <Card withBorder={false} bg={'transparent'} radius={0}>
                            <Stack gap="sm">
                                <Title order={2}>{member.title}</Title>

                                {member.about ? (
                                    <Text component="div" dangerouslySetInnerHTML={{__html: member.about}}/>
                                ) : (
                                    <Text c="dimmed">No biography provided.</Text>
                                )}

                                <Stack gap={4} mt="md">
                                    {socials.length > 0 && (
                                        <Group justify="center" mt="sm">
                                            {socials.map((s) => (
                                                <Button
                                                    key={s.label}
                                                    component="a"
                                                    href={s.href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    size="xs"
                                                    variant="outline"
                                                >
                                                    {s.label}
                                                </Button>
                                            ))}
                                        </Group>
                                    )}
                                </Stack>
                            </Stack>
                        </Card>
                    </Grid.Col>
                </Grid>
            </>
        );
    }

    return (
        <section className={classes.sOurPro}>
            <Container>
                <Group mb="md" gap="sm">
                    <Button variant="outline" onClick={() => navigate(-1)}>
                        Back
                    </Button>
                </Group>
                <TitleDecor mb="3rem">
                    Meet <span>the Pros</span>
                </TitleDecor>
                {content}
            </Container>
        </section>
    );
};
